import {
  signInWithCustomToken,
  signOut as firebaseSignOut,
} from "firebase/auth";
import { parseISO } from "date-fns";
import { auth } from "utilities/firebase";
import { getDeviceId } from "utilities/getDeviceId";
import lastSession, { type ILastSession } from "utilities/lastSession";
import { useAuthStore } from "../stores/authStore";
import { useShellStore } from "@/features/shell/stores/shellStore";
import { telemetry } from "@/utilities/telemetry";
import { message } from "utilities/message/message";
import type { ValidationError } from "../utils/authFetch";
import { type AuthError, authFetch } from "../utils/authFetch";
import { err, ok, type Result } from "@/utilities/result";
import {
  identifyAnalytics,
  resetAnalytics,
  sendNativeIdentifyIfNeeded,
} from "../utils/analytics";
import {
  isAuthedSessionResponse,
  type AuthedSessionResponse,
  type CustomerLicence,
  type SessionResponse,
  type SignUpProfile,
} from "../models/session";
import {
  DEFAULT_ATTRIBUTES,
  isAuthed,
  type AuthedSession,
  type GuestSession,
} from "../models/authState";
import type { AuthState } from "../stores/authStore";
import { OTP_FLOW } from "../utils/constants";
import { BET_STOP_MODAL_ID_LOGOUT } from "@/features/auth/components/BetStopModalLogout";
import { attempt } from "@/utilities/attempt";
import { captureMessage } from "@sentry/gatsby";

const WAF_API_KEY = process.env.GATSBY_WAF_API_KEY;
const API_URI = process.env.GATSBY_API_URI;

let inflightRefresh: Promise<Result<SessionResponse, AuthError>> | null = null;

const transition = (state: AuthState) => {
  useAuthStore.getState().transition(state);
};

const toAuthedSession = (response: AuthedSessionResponse): AuthedSession => {
  return {
    sessionId: response.id,
    userId: response.firebaseToken.uid,
    token: response.token,
    expiresAt: parseISO(response.expiresAt ?? response.expires).getTime(),
    permissions: response.permissions,
    firebase: response.firebaseToken,
    attributes: {
      ...DEFAULT_ATTRIBUTES,
      ...response.attributes,
    },
    lastRefreshedAt: Date.now(),
  };
};

const toGuestSession = (response: SessionResponse): GuestSession => ({
  permissions: response.permissions,
  attributes: {
    ...DEFAULT_ATTRIBUTES,
    ...response.attributes,
  },
  lastRefreshedAt: Date.now(),
});

export const initialize = async () => {
  const state = useAuthStore.getState();
  await refresh({ forceGuest: !isAuthed(state) });
};

export const refresh = async (params?: {
  forceGuest?: boolean;
  isImpersonation?: boolean;
}): Promise<Result<SessionResponse, AuthError>> => {
  if (inflightRefresh) await inflightRefresh;

  inflightRefresh = _doRefresh(params).finally(() => {
    inflightRefresh = null;
  });

  return inflightRefresh;
};

type SignInEmailParams = {
  email: string;
  password: string;
};

export const signInWithSessionObject = async (
  session: AuthedSessionResponse,
) => {
  await signInWithCustomToken(auth, session.firebaseToken.token);

  telemetry.loginAttempt({ outcome: "success", source: "biometrics" });

  identifyAnalytics(session.userId);

  transition({
    status: "authenticated",
    session: toAuthedSession(session),
  });
};

export const signIn = async ({ email, password }: SignInEmailParams) => {
  const result = await authFetch<AuthedSessionResponse>("/auth", {
    method: "POST",
    body: { email: email.trim(), password, deviceId: getDeviceId() },
    useWaf: true,
  });

  if (!result.ok) {
    const { messageTemplate } = result.error;

    if (messageTemplate === "user.closed.nserMatch") {
      useShellStore.getState().setModal(BET_STOP_MODAL_ID_LOGOUT);
    }

    if (messageTemplate === "waf-token-rejected") {
      return verifyCaptcha({ email: email.trim(), password });
    }

    // collect more info. will handle later based on data
    if (messageTemplate === "waf-token-absent") {
      captureMessage(`WAF token missing on sign in`, {
        level: "warning",
        fingerprint: ["waf-sign-in", messageTemplate],
        tags: {
          messageTemplate,
          hasIntegration: String(!!(window as any)?.AwsWafIntegration),
          hasCaptcha: String(!!(window as any)?.AwsWafCaptcha),
        },
      });
    }

    telemetry.loginAttempt({
      email,
      outcome: "failure",
      source: "credentials",
      errorMessage: result.error.message,
      errorMessageTemplate: result.error.messageTemplate,
      statusCode: result.error.statusCode,
    });

    return result;
  }

  const response = result.data;

  // Sign in to Firebase
  const userCredentials = await signInWithCustomToken(
    auth,
    response.firebaseToken.token,
  );
  console.log(
    `Obtained firebase credentials for user: ${userCredentials?.user?.uid}. Is anonymous: ${userCredentials?.user?.isAnonymous}`,
  );

  lastSession.set({ uid: response.firebaseToken.uid, email } as ILastSession);

  telemetry.loginAttempt({ email, outcome: "success", source: "credentials" });
  identifyAnalytics(response.userId);

  transition({
    status: "authenticated",
    session: toAuthedSession(response),
  });

  return result;
};

type CaptchaAPI = {
  renderCaptcha: (
    container: HTMLElement | null,
    options: {
      apiKey?: string;
      onLoad: () => void;
      onSuccess: () => void;
      onPuzzleIncorrect: () => void;
    },
  ) => void;
};

export const verifyCaptcha = ({ email, password }: SignInEmailParams) => {
  return new Promise<
    Awaited<ReturnType<typeof authFetch<AuthedSessionResponse>>>
  >((resolve) => {
    useShellStore
      .getState()
      .setCaptchaPuzzle({ required: true, visible: false });

    setTimeout(() => {
      const AwsCaptcha = (window as unknown as { AwsWafCaptcha?: CaptchaAPI })
        ?.AwsWafCaptcha;

      AwsCaptcha?.renderCaptcha(document.getElementById("captcha-container"), {
        apiKey: WAF_API_KEY,
        onLoad: () => {
          if (useShellStore.getState().captcha.required) {
            useShellStore
              .getState()
              .setCaptchaPuzzle({ required: true, visible: true });
          }
        },
        onSuccess: async () => {
          useShellStore.getState().closeCaptchaPuzzle();
          const result = await signIn({ email, password });
          useShellStore.getState().setModal(null);
          resolve(result);
        },
        onPuzzleIncorrect: () => {
          useShellStore.getState().closeCaptchaPuzzle();
          resolve(
            err({
              messageTemplate: "captcha.puzzle.failed",
              message: "The CAPTCHA was entered incorrectly. Please try again.",
            }),
          );
        },
      });
    }, 300);
  });
};

type SignUpEmailParams = {
  email: string;
  password: string;
  profile: SignUpProfile;
  deviceId: string;
};

export const signUp = async ({
  email,
  password,
  profile,
  deviceId,
}: SignUpEmailParams) => {
  const result = await authFetch<AuthedSessionResponse>("/customers", {
    method: "POST",
    body: { email, password, confirmPassword: password, deviceId, profile },
  });

  if (!result.ok) {
    return result;
  }

  const response = result.data;

  // Sign into Firebase
  const userCredentials = await signInWithCustomToken(
    auth,
    response.firebaseToken.token,
  );
  console.log(
    `Obtained firebase credentials for user: ${userCredentials?.user?.uid}. Is anonymous: ${userCredentials?.user?.isAnonymous}`,
  );

  lastSession.set({ uid: response.firebaseToken.uid, email } as ILastSession);

  identifyAnalytics(response.userId, {
    email,
  });

  transition({
    status: "authenticated",
    session: toAuthedSession(response),
  });

  telemetry.completedProfileIdentity({
    email,
    country: profile.country || "",
    dateOfBirth: profile.dateOfBirth || "",
    depositLimit: profile.depositLimit?.toString() || "",
    depositLimitPeriod: profile.depositLimitPeriod?.toString() || "",
    firstName: profile.firstName || "",
    hasAffiliateCode: !!profile.promotionCode,
    lastName: profile.lastName || "",
    phone: profile.phone || "",
    postCode: profile.postCode || "",
    promotionCode: profile.promotionCode || "",
    showLimits: profile.showLimits || false,
    state: profile.state || "",
    street: profile.street || "",
    streetNumber: profile.streetNumber || "",
    suburb: profile.suburb || "",
    terms: profile.terms || false,
    unit: profile.unit || "",
  });

  return result;
};

type SignOutParams = {
  fetchOptions?: { onSuccess?: () => void };
};

export const signOut = async (params?: SignOutParams) => {
  const state = useAuthStore.getState();
  const token = isAuthed(state) ? state.session.token : null;

  useAuthStore.getState().setFlags({ isSigningOut: true });

  try {
    if (token) {
      // Token might be expired, ignore errors
      await authFetch("/auth/revoke-session", { method: "POST", token });
    }

    localStorage.removeItem("userPreferences");
    Object.values(OTP_FLOW).forEach((flow) => {
      sessionStorage.removeItem(flow);
    });

    // Get a guest session
    const guestResult = await refresh({ forceGuest: true });

    await firebaseSignOut(auth);

    params?.fetchOptions?.onSuccess?.();

    telemetry.loggedOut();
    resetAnalytics();

    return guestResult;
  } finally {
    useAuthStore.getState().setFlags({ isSigningOut: false });
  }
};

export const ingest = async (apiToken: string, firebaseToken: string) => {
  // Sign in to Firebase with the impersonation token
  await signInWithCustomToken(auth, firebaseToken);

  // Set a temporary impersonating state with the API token so refresh() can authenticate
  transition({
    status: "impersonating",
    session: {
      sessionId: "",
      userId: "",
      token: apiToken,
      expiresAt: 0,
      permissions: {},
      firebase: { url: null, token: null, expires: null, uid: null },
      attributes: DEFAULT_ATTRIBUTES,
      lastRefreshedAt: 0,
    },
  });

  return await refresh({ isImpersonation: true });
};

export const handleUnauthorized = async () => {
  await refresh({ forceGuest: true });
};

export const forgetPassword = ({
  email,
  dateOfBirth,
}: { email: string; dateOfBirth: string }) =>
  authFetch("/auth/reset-password", {
    method: "POST",
    body: { email, dateOfBirth },
  });

export const resetPassword = ({
  token,
  newPassword,
}: { token: string; newPassword: string }) =>
  authFetch(`/auth/reset-password/${token}`, {
    method: "POST",
    body: { newPassword },
    useWaf: true,
  });

export const migrateProfile = async () => {
  if (!isAuthed(useAuthStore.getState())) return;
  await message("migrate-user-profile", {});
};

export const validateProfile = async (
  email: string,
  password: string,
  profile: SignUpProfile,
): Promise<
  [
    ValidationError[],
    CustomerLicence | null,
    { zendeskDomain: string; zendeskKey: string } | null,
  ]
> => {
  const response = await fetch(`${API_URI}/customers/validations`, {
    method: "POST",
    mode: "cors",
    headers: {
      "Content-Type": "application/json",
      "X-DeviceId": getDeviceId(),
    },
    body: JSON.stringify({
      email,
      password,
      confirmPassword: password,
      profile,
    }),
  });

  const json = await response.json();

  if (response.ok) {
    return [[], null, null];
  }

  const allowedKeys = [
    email ? "email" : null,
    password ? "password" : null,
    ...Object.keys(profile),
  ].filter(Boolean);

  return [
    (json?.errors || []).filter(
      (err: ValidationError) =>
        allowedKeys.includes(err.propertyPath) ||
        allowedKeys.includes(err.propertyPath?.replace("profile.", "")),
    ),
    json?.licence || null,
    json?.attributes || null,
  ];
};

export const getGeoInfo = async () => {
  try {
    const response = await fetch(`${API_URI}/customers/geoIpInformation`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      return err(response);
    }

    const json = await attempt(response.json());
    if (!json.ok) {
      return err(json.error);
    }
    return ok(json.data);
  } catch (e) {
    return err(e);
  }
};

const _doRefresh = async (params?: {
  forceGuest?: boolean;
  isImpersonation?: boolean;
}): Promise<Result<SessionResponse, AuthError>> => {
  const state = useAuthStore.getState();
  const isGuest = params?.forceGuest ?? false;
  const isImpersonation = params?.isImpersonation ?? false;
  const token = !isGuest && isAuthed(state) ? state.session.token : null;

  useAuthStore.getState().setFlags({ isRefreshing: true });

  try {
    const result = await authFetch<SessionResponse>("/auth", {
      method: "GET",
      token: isGuest ? null : token,
    });

    if (!result.ok) {
      // Only retry as guest if we weren't already trying as guest
      if (!isGuest) {
        return await _doRefresh({ forceGuest: true });
      }
      return result;
    }

    const response = result.data;

    if (isAuthedSessionResponse(response) && !isGuest) {
      const currentStatus =
        isImpersonation || state.status === "impersonating"
          ? "impersonating"
          : "authenticated";

      const session = toAuthedSession(response);
      // GET /auth refresh doesn't return the session token — preserve existing
      if (!session.token && token) {
        session.token = token;
      }
      transition({ status: currentStatus, session });
    } else {
      transition({ status: "guest", session: toGuestSession(response) });
    }

    // App only — when they open the app and they are logged in, send identify
    sendNativeIdentifyIfNeeded(
      isAuthedSessionResponse(response) ? response.userId : null,
    );

    return result;
  } finally {
    useAuthStore.getState().setFlags({ isRefreshing: false });
  }
};
